import { useSelector } from "react-redux";
import {
  tokenDecoder,
  IsServiceTypeVendor,
  replaceVideoUrl,
} from "../lib/utils";
import {
  BadgeDollarSignIcon,
  DollarSignIcon,
  Lock,
  LogOut,
  Settings,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../@/components/ui/dropdown-menu";
import { MdOutlineMenu } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useDeleteNotificationTokenMutation,
  useGetVendorDetailsQuery,
} from "../api/api";
import { clearToken } from "../redux/slices/authSlice";
import { useContext, useEffect, useState } from "react";
import NotificationIcon from "./common/NotificationIcon";
import { NotificationContext } from "../components/ProtectedRoute";
import { APP_MODULE } from "../lib/constants";
import { autoFormatDate } from "../lib/utils";
import BannerDetail from "./BannerDetail";
declare global {
  interface Window {
    Tawk_API?: {
      setAttributes: (attributes: { name?: string; email?: string }) => void;
      hide?: () => void;
      minimize?: () => void;
      endChat?: () => void;
    };
  }
}

interface TopbarProps {
  handleLogout: () => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
}
const Topbar: React.FC<TopbarProps> = ({
  handleLogout,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  const notification = useContext(NotificationContext);
  const [orderNotifications, setOrderNotifications] = useState<any[]>([]);
  const [totalNotificationCount, setTotalNotificationCount] = useState(0);
  const token = useSelector((state: any) => state.auth.token);
  const appState = useSelector((state: any) => state.appState);
  const decodedToken = tokenDecoder(token);
  const { data: profileData, isLoading: profileDataLoading } =
    useGetVendorDetailsQuery({
      vendorId: decodedToken.vendorId,
    });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [delteNotificationToken, { isLoading }] =
    useDeleteNotificationTokenMutation();

  useEffect(() => {
    if (notification && notification.data) {
      if (notification.data.module === APP_MODULE.LOGOUT) {
        setTimeout(handleLogout, 2000);
      }
      console.log(document.hasFocus());
      if (!document.hasFocus()) {
        console.log("Not focused");
        const desktopNotification = new Notification(
          notification.notification?.title || "New Notification",
          {
            body:
              notification.notification?.body || "You have a new notification",
            icon: "/logo192.png",
          }
        );

        desktopNotification.onclick = () => {
          window.focus();
          if (notification.data.entityId !== "undefined") {
            navigate(`/edit-order/${notification.data.entityId}`);
          }
        };
      }

      setOrderNotifications((prevNotification) => [
        ...prevNotification,
        { ...notification, seen: false },
      ]);
      setTotalNotificationCount((prevCount: any) => prevCount + 1);
    }
  }, [notification]);

  useEffect(() => {
    if (profileData && profileData.data) {
      const fullName =
        `${profileData.data.firstName} ${profileData.data.lastName}`.trim();
      const userName = profileData.data.company
        ? `${fullName} - ${profileData.data.company}`
        : fullName;
      const userEmail = profileData.data.email;

      if (window.Tawk_API?.setAttributes && userName && userEmail) {
        const checkTawkAPI = setInterval(() => {
          try {
            if (window.Tawk_API && window.Tawk_API.setAttributes) {
              window.Tawk_API.setAttributes({
                name: userName,
                email: userEmail,
              });
              clearInterval(checkTawkAPI);
            }
          } catch (error) {
            clearInterval(checkTawkAPI);
          }
        }, 500);

        setTimeout(() => clearInterval(checkTawkAPI), 10000);
      }
    }
  }, [profileData]);

  useEffect(() => {
    let notifications: any = localStorage.getItem(
      `${decodedToken.role}_${decodedToken.vendorId}_notifications`
    );
    if (notifications) {
      notifications = JSON.parse(notifications);
    } else {
      notifications = [];
    }
    const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);

    const filteredNotifications = notifications
      .filter((notification: any) => {
        const notificationDate = new Date(notification.data.timestamp);
        return (
          notificationDate >= twoWeeksAgo && !isNaN(notificationDate.getTime())
        );
      })
      .sort((a: any, b: any) => {
        const dateA = new Date(a.data.timestamp);
        const dateB = new Date(b.data.timestamp);
        return dateB.getTime() - dateA.getTime();
      });

    if (filteredNotifications.length > 0) {
      localStorage.setItem(
        `${decodedToken.role}_${decodedToken.vendorId}_notifications`,
        JSON.stringify(filteredNotifications)
      );
      setOrderNotifications(filteredNotifications);
      const totalNotificationCount = filteredNotifications.filter(
        (notification: any) => !notification.seen
      ).length;
      setTotalNotificationCount(totalNotificationCount);
    }
  }, [decodedToken.role, decodedToken.vendorId, notification]);

  const logout = async () => {
    if (window.Tawk_API) {
      try {
        if (window.Tawk_API?.hide) {
          window.Tawk_API.hide();
        }

        if (window.Tawk_API.endChat) {
          await window.Tawk_API.endChat();
        }

        setTimeout(() => {
          if (window.Tawk_API?.minimize) {
            window.Tawk_API.minimize();
          }
          if (window.Tawk_API?.hide) {
            window.Tawk_API.hide();
          }
        }, 500);

        if (window.Tawk_API.setAttributes) {
          window.Tawk_API.setAttributes({
            name: undefined,
            email: undefined,
          });
        }
      } catch (error) {
        console.error("Tawk API error:", error);
      }
    }

    // Remove all Tawk.to related items from local storage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("twk")) {
        localStorage.removeItem(key);
      }
    });

    // Remove Tawk.to related items from session storage
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("tawk_sv")) {
        sessionStorage.removeItem("tawk_sv");
      }
    });

    await delteNotificationToken({
      deleteTokenDto: {
        userId: decodedToken.vendorId,
      },
    }).unwrap();

    handleLogout();
  };

  const handleNotificationClicked = (notification: any) => {
    if (notification.data.entityId !== "undefined") {
      navigate(`/edit-order/${notification.data.entityId}`);
    }
  };

  const renderItem = ({ item }: { item: any }) => {
    let iconSource;

    switch (item.status) {
      case "success":
        iconSource = "/Images/DashboardImages/notificationBell.png";
        break;
      case "error":
        iconSource = "/Images/NotificationImages/orderCancelled.png";
        break;
      case "active":
        iconSource = "/Images/NotificationImages/orderSuccess.png";
        break;
      case "delivered":
        iconSource = "/Images/NotificationImages/orderSuccessFull.png";
        break;
      case "confirmed":
        iconSource = "/Images/NotificationImages/orderSuccess.png";
        break;
      case "cancelled":
        iconSource = "/Images/NotificationImages/orderCancelled.png";
        break;
      default:
        iconSource = "/Images/DashboardImages/notificationBell.png";
    }
    return iconSource;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp).toString();
    return autoFormatDate(date);
  };

  const handleOpenNotificationDropdown = () => {
    const updatedNotifications = orderNotifications.map(
      (notification: any) => ({
        ...notification,
        seen: true,
      })
    );
    localStorage.setItem(
      `${decodedToken.role}_${decodedToken.vendorId}_notifications`,
      JSON.stringify(updatedNotifications)
    );
    setTotalNotificationCount(0);
  };

  return (
    <div className="w-full min-h-16 bg-white flex justify-between select-none items-center px-6 border-b border-gray-300 z-50 sticky top-0  ">
      <div className="flex items-center gap-4">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="md:hidden block"
        >
          {isSidebarOpen ? <></> : <MdOutlineMenu className="w-6 h-6" />}
        </button>
        <p className="font-medium text-left text-xl text-black capitalize">
          {appState?.routeName?.toLowerCase().includes("configuration")
            ? "Admin Configuration"
            : appState?.routeName}
        </p>
      </div>

      <div className="flex-1 flex justify-center">
        <BannerDetail />
      </div>

      <div className="flex items-center gap-6">
        <div className="hidden md:flex items-center gap-2">
          {profileDataLoading ? (
            <div className="w-20 h-6 bg-gray-300 animate-pulse rounded"></div>
          ) : (
            <p className="font-medium text-black">
              {decodedToken.role === "VENDOR"
                ? `${profileData?.data?.firstName} ${profileData?.data?.lastName}`
                : "Admin Account"}
            </p>
          )}
        </div>
        <DropdownMenu onOpenChange={handleOpenNotificationDropdown}>
          <DropdownMenuTrigger asChild>
            <div>
              <NotificationIcon count={totalNotificationCount} />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="mr-5 w-[50vh] h-[50vh] overflow-y-auto">
            <DropdownMenuLabel className="text-lg">
              Notifications
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {orderNotifications.length > 0 ? (
                (() => {
                  const today = new Date();
                  const todayString = today.toDateString();

                  const todayNotifications = orderNotifications.filter(
                    (notification) => {
                      const notificationDate = new Date(
                        notification.data.timestamp
                      ).toDateString();
                      return notificationDate === todayString;
                    }
                  );

                  const olderNotifications = orderNotifications.filter(
                    (notification) => {
                      const notificationDate = new Date(
                        notification.data.timestamp
                      ).toDateString();
                      return notificationDate !== todayString;
                    }
                  );

                  return (
                    <div>
                      {/* Render today's notifications */}
                      {todayNotifications.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontWeight: "bold",
                              padding: "2px 5px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>Today</span>
                          </div>
                          {todayNotifications.map((notification, index) => {
                            const imgPath = renderItem({
                              item: notification.data,
                            });
                            return (
                              <DropdownMenuItem
                                key={index}
                                className="cursor-pointer p-2"
                                onClick={() =>
                                  handleNotificationClicked(notification)
                                }
                              >
                                <div className="flex flex-row items-center w-full">
                                  <img
                                    className="w-6 h-5 mr-2.5 rounded-[50%]"
                                    src={imgPath}
                                    alt="NotificationIcon"
                                  />
                                  <div className="flex flex-col w-full">
                                    {notification.notification?.title && (
                                      <div
                                        style={{
                                          padding: "8px 0",
                                          display: "flex",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <div className="font-bold">
                                          {notification.notification.title}
                                        </div>
                                        <div
                                          style={{
                                            alignItems: "flex-end text-wrap",
                                          }}
                                        >
                                          {formatTime(
                                            notification.data.timestamp
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {notification.notification?.body && (
                                      <div>
                                        {notification.notification.body}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </DropdownMenuItem>
                            );
                          })}
                        </div>
                      )}

                      {/* Render older notifications */}
                      {olderNotifications.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontWeight: "bold",
                              padding: "2px 5px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>Older</span>
                          </div>
                          {olderNotifications.map((notification, index) => {
                            const imgPath = renderItem({
                              item: notification.data,
                            });
                            return (
                              <DropdownMenuItem
                                key={index}
                                className="cursor-pointer p-2"
                                onClick={() =>
                                  handleNotificationClicked(notification)
                                }
                              >
                                <div className="flex flex-row items-center w-full">
                                  <img
                                    className="w-6 h-5 rounded-full mr-2.5"
                                    src={imgPath}
                                    alt="NotificationIcon"
                                  />
                                  <div className="flex flex-col w-full">
                                    {notification.notification?.title && (
                                      <div
                                        style={{
                                          padding: "8px 0",
                                          display: "flex",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <div className="font-bold">
                                          {notification.notification.title}
                                        </div>
                                        <div style={{ alignItems: "flex-end" }}>
                                          {formatTime(
                                            notification.data.timestamp
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {notification.notification?.body && (
                                      <div>
                                        {notification.notification.body}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </DropdownMenuItem>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="p-2 text-sm text-gray-500 text-center">
                  No Notifications Available.
                </div>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {profileDataLoading ? (
              <div className="w-10 h-10 rounded-full bg-gray-300 animate-pulse"></div>
            ) : (
              <img
                src={
                  decodedToken.role === "ADMIN"
                    ? "/logo192.png"
                    : replaceVideoUrl(profileData?.data?.vendorImage || "") ||
                      "/storeDummyImg.png"
                }
                alt="User Avatar"
                className="w-10 h-10 rounded-full cursor-pointer border border-gray-400"
                onError={(e) => {
                  e.currentTarget.src = "/storeDummyImg.png";
                }}
              />
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <div className="md:hidden px-4 py-2 flex flex-col items-center border-b">
              {profileDataLoading ? (
                <div className="w-16 h-16 rounded-full bg-gray-300 animate-pulse mb-2"></div>
              ) : (
                <img
                  src={
                    decodedToken.role === "ADMIN"
                      ? "/logo192.png"
                      : profileData?.data?.vendorImage || "/storeDummyImg.png"
                  }
                  alt="User Avatar"
                  className="w-16 h-16 rounded-full border border-gray-400 mb-2"
                  onError={(e) => {
                    e.currentTarget.src = "/storeDummyImg.png";
                  }}
                />
              )}
              {profileDataLoading ? (
                <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
              ) : (
                <span className="font-medium text-center">
                  {decodedToken.role === "VENDOR"
                    ? `${profileData?.data?.firstName} ${profileData?.data?.lastName}`
                    : "Admin Account"}
                </span>
              )}
            </div>
            <div className="hidden md:block">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  if (decodedToken.role === "VENDOR") {
                    navigate("/reset-password");
                  } else {
                    navigate("/reset-password?isAdmin=true");
                  }
                }}
              >
                <Lock className="mr-2 h-4 w-4" />
                <span>Reset Password</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            {decodedToken.role === "ADMIN" && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  navigate("/payouts");
                }}
              >
                <DollarSignIcon className="mr-2 h-4 w-4" />
                <span>Payout</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default Topbar;
